import React, { useState } from "react";
import { toast } from "react-toastify";
import { responseMessage } from "../../../services/platformService";
import { EmptyState, FormDrawer, LoadState, PrimaryButton, cardStyle, iconBtnStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad, withNulls } from "../../../components/ui";

// One master-data list (legal entities, tax rates, lookups ...) with an add / edit drawer
// and activate / deactivate. Nothing is deleted: a row is switched off.
//
//   columns: [[label, row => value]]
//   fields(values): FormFields descriptors (a function so they can depend on the current values)
//   load(): API answer with data: rows          save(id | null, data): API answer
//   toForm(row): the edit form's starting values (map reference codes back to dropdown values here:
//                ids are encrypted afresh in every response, so match on the stable code the API returns)
//   fieldKeys: every key the form edits (cleared ones are sent as null on edit)
//   lockedOnEdit: keys that cannot change after create (shown disabled)
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };

export default function MasterSection({ noun, columns, fields, load, save, toForm = (r) => r, fieldKeys, lockedOnEdit = [], defaults = {}, emptyText, canEdit = true, deps = [], extraActions, onChanged }) {
  const { data, loading, error, reload } = useLoad(load, deps);
  const [edit, setEdit] = useState(undefined); // undefined closed, null new, row edit
  const rows = data || [];

  const done = () => {
    reload();
    onChanged && onChanged();
  };

  const setStatus = async (row, status) => {
    const res = await save(row.id, { status }).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) {
      toast.success(status === "ACTIVE" ? "Activated" : "Deactivated");
      done();
    } else toast.error(responseMessage(res));
  };

  const editing = edit && edit !== null;
  const formFields = (values) =>
    fields(values).map((f) => (editing && lockedOnEdit.includes(f.key) ? { ...f, disabled: true, hint: f.hint || "Cannot be changed once created" } : f));

  return (
    <>
      {canEdit ? (
        <div className="mb-3">
          <PrimaryButton onClick={() => setEdit(null)}>Add {noun}</PrimaryButton>
        </div>
      ) : null}
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title={`No ${noun}s yet`} text={emptyText} />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  {[...columns.map(([label]) => label), "Status", ""].map((h, i) => (
                    <th key={`${h}-${i}`} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    {columns.map(([label, render]) => (
                      <td key={label} style={tdStyle}>
                        {render(r) ?? "—"}
                      </td>
                    ))}
                    <td style={tdStyle}>
                      <span style={statusBadge(r.status || "ACTIVE")}>{statusLabel(r.status || "ACTIVE")}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      {extraActions ? extraActions(r, done) : null}
                      {canEdit ? (
                        <>
                          <button type="button" style={linkBtn} onClick={() => setEdit(r)}>
                            Edit
                          </button>{" "}
                          <button type="button" style={linkBtn} onClick={() => setStatus(r, r.status === "INACTIVE" ? "ACTIVE" : "INACTIVE")}>
                            {r.status === "INACTIVE" ? "Activate" : "Deactivate"}
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={edit !== undefined}
        title={edit ? `Edit ${noun}` : `Add ${noun}`}
        initial={edit ? toForm(edit) : defaults}
        fields={formFields}
        onHide={() => setEdit(undefined)}
        onSave={(clean, raw) => (edit ? save(edit.id, withNulls(raw, fieldKeys.filter((k) => !lockedOnEdit.includes(k)))) : save(null, clean))}
        onDone={done}
      />
    </>
  );
}
